import React, { useState } from 'react';
import { inr, pct } from './portfolioHealthCheckApi';

/**
 * Renders the fund overlap analysis and portfolio breakdown.
 * Includes the "Different funds, some of the same companies" card
 * and collapsible dropdowns for detailed comparisons with full cobrowsing sync.
 */
export default function PortfolioAnalysis({ analysis, isClient = false, uiState = {}, onUiChange }) {
  const { result, fundNames, notCompared, coverageSentence, synthetic } = analysis;
  const name = (k) => fundNames[k] || 'Fund';
  const pairs = [...result.pairs].sort((a, b) => b.confirmed_overlap - a.confirmed_overlap);
  
  const [openPair, setOpenPair] = useState(pairs[0] ? `${pairs[0].a}|${pairs[0].b}` : null);
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [showAllShared, setShowAllShared] = useState(false);

  // Cobrowsing synced UI state
  const effectiveOpenPair = isClient
    ? (uiState.openPair ?? (pairs[0] ? `${pairs[0].a}|${pairs[0].b}` : null))
    : (uiState.openPair ?? openPair);
  const effectiveExpandedCompanies = isClient
    ? (uiState.expandedCompanies || {})
    : (uiState.expandedCompanies ?? expandedCompanies);
  const effectiveShowAllShared = isClient
    ? Boolean(uiState.showAllShared)
    : (uiState.showAllShared !== undefined ? uiState.showAllShared : showAllShared);
  const openAccordions = uiState.openAccordions || {};

  const handleToggleAccordion = (key, isOpen) => {
    if (isClient) return;
    const next = { ...openAccordions, [key]: isOpen };
    onUiChange?.({ openAccordions: next });
  };

  const p = result.portfolio;
  const blocking = result.issues.filter((i) => i.severity !== 'info');

  // Shared companies calculation
  const exposures = p?.exposures || [];
  const multiFundCompanies = exposures.filter((e) => e.contributors && e.contributors.length >= 2);
  const singleFundCompanies = exposures.filter((e) => !e.contributors || e.contributors.length <= 1);

  const multiFundExposureAmount = multiFundCompanies.reduce((sum, e) => sum + (e.amount || 0), 0);
  const singleFundExposureAmount = singleFundCompanies.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalIdentifiedAmount = multiFundExposureAmount + singleFundExposureAmount;

  const multiFundPct = totalIdentifiedAmount > 0 ? (multiFundExposureAmount / totalIdentifiedAmount) * 100 : 0;
  const singleFundPct = totalIdentifiedAmount > 0 ? (singleFundExposureAmount / totalIdentifiedAmount) * 100 : 0;

  const displayedSharedCompanies = effectiveShowAllShared ? multiFundCompanies : multiFundCompanies.slice(0, 5);

  return (
    <div className="phc-analysis-wrapper">
      {/* Main Analysis Header Card */}
      <section className="phc-card" aria-labelledby="phc-an-h">
        <p className="phc-eyebrow">Fund overlap analysis{synthetic ? ' · synthetic data' : ''}</p>
        <h2 id="phc-an-h" className="phc-h2">
          {result.funds.length} funds compared · {pairs.length} pair{pairs.length === 1 ? '' : 's'} checked
        </h2>
        <p className="phc-muted">{coverageSentence}</p>
        {result.dates.earliest && (
          <p className="phc-small">
            Holdings disclosed {result.dates.earliest === result.dates.latest ? `as of ${result.dates.earliest}` : `between ${result.dates.earliest} and ${result.dates.latest}`}
            {result.dates.materially_different ? ' — dates differ materially, so overlap is approximate.' : '.'}
          </p>
        )}
      </section>

      {/* Different funds, some of the same companies Card */}
      {p && (
        <section className="phc-card phc-shared-companies-card">
          <h2 className="phc-shared-title">Different funds, some of the same companies</h2>
          <p className="phc-shared-subtitle">
            Some company exposure comes through several funds; some comes through just one. Here is the split within the stocks we could identify.
          </p>

          <div className="phc-shared-stats-grid">
            <div className="phc-shared-stat-col">
              <span className="phc-shared-stat-label">Companies in 2+ funds</span>
              <strong className="phc-shared-stat-pct">{multiFundPct.toFixed(1)}%</strong>
              <span className="phc-shared-stat-amount">{inr(multiFundExposureAmount)} in stock exposure</span>
            </div>
            <div className="phc-shared-stat-col">
              <span className="phc-shared-stat-label">Companies in only one</span>
              <strong className="phc-shared-stat-pct">{singleFundPct.toFixed(1)}%</strong>
              <span className="phc-shared-stat-amount">{inr(singleFundExposureAmount)} in stock exposure</span>
            </div>
          </div>

          <p className="phc-shared-body-text">
            Shared companies are not automatically a problem. This shows where your funds meet — not money invested twice, wasted money, or a percentage of your entire portfolio.
          </p>

          <p className="phc-shared-split-text">
            The split covers {inr(totalIdentifiedAmount)} in identified company shares, in the compared funds only. Debt, cash, other assets and funds not compared are outside it.
          </p>

          <div className="phc-scope-tag-row">
            <span className="phc-scope-tag">Scope and unmapped holdings</span>
          </div>

          {/* List of 2+ funds companies with expand/collapse */}
          <div className="phc-shared-list-section">
            <h3 className="phc-shared-list-heading">
              {multiFundCompanies.length} companies held by two or more funds · {inr(multiFundExposureAmount)}. Largest:
            </h3>

            <div className="phc-shared-companies-list">
              {displayedSharedCompanies.map((e) => {
                const isExpanded = !!effectiveExpandedCompanies[e.identity_id];
                return (
                  <div key={e.identity_id} className="phc-shared-company-row">
                    <div className="phc-shared-company-top">
                      <span className="phc-shared-company-name">{e.display_name}</span>
                      <button
                        type="button"
                        className="phc-shared-company-toggle-btn"
                        disabled={isClient}
                        style={isClient ? { cursor: 'default' } : undefined}
                        onClick={() => {
                          if (isClient) return;
                          const next = {
                            ...effectiveExpandedCompanies,
                            [e.identity_id]: !effectiveExpandedCompanies[e.identity_id],
                          };
                          setExpandedCompanies(next);
                          onUiChange?.({ expandedCompanies: next });
                        }}
                        aria-expanded={isExpanded}
                      >
                        {inr(e.amount)} · {e.contributors.length} funds {isExpanded ? '▴' : '▾'}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="phc-shared-breakdown-box">
                        {e.contributors.map((c) => {
                          const fundTitle = name(c.fund_key);
                          const sharePct = (c.share * 100).toFixed(1);
                          const fundAmount = (e.amount || 0) * c.share;
                          return (
                            <div key={c.fund_key} className="phc-contributor-item">
                              <span className="phc-contributor-name">{fundTitle}</span>
                              <span className="phc-contributor-val">
                                {sharePct}% ({inr(fundAmount)})
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {multiFundCompanies.length > 5 && (
              <button
                type="button"
                className="phc-link phc-view-more-shared-btn"
                disabled={isClient}
                style={isClient ? { cursor: 'default' } : undefined}
                onClick={() => {
                  if (isClient) return;
                  const next = !effectiveShowAllShared;
                  setShowAllShared(next);
                  onUiChange?.({ showAllShared: next });
                }}
              >
                {effectiveShowAllShared ? 'Show less' : `View more (${multiFundCompanies.length - 5})`}
              </button>
            )}
          </div>
        </section>
      )}

      {/* Explore detailed comparisons Section with Collapsible Dropdowns */}
      <div className="phc-detailed-comparisons-container">
        <h3 className="phc-detailed-section-title">Explore detailed comparisons</h3>

        {/* 1. Fund pairs */}
        {pairs.length > 0 && (
          <details
            className="phc-card phc-accordion-card"
            open={openAccordions.pairs !== undefined ? openAccordions.pairs : false}
            onToggle={(e) => handleToggleAccordion('pairs', e.target.open)}
          >
            <summary
              className="phc-accordion-summary"
              onClick={(e) => { if (isClient) e.preventDefault(); }}
              style={isClient ? { cursor: 'default' } : undefined}
            >
              <span>Fund pairs</span>
              <span className="phc-accordion-indicator">▾</span>
            </summary>
            <div className="phc-accordion-content">
              <h4 className="phc-h4">Highest-overlap pairs</h4>
              <ol className="phc-pairs">
                {pairs.slice(0, 5).map((x) => (
                  <li key={`${x.a}|${x.b}`}>
                    <span>
                      {name(x.a)} <span className="phc-muted">&amp;</span> {name(x.b)}
                    </span>
                    <span className="phc-num">
                      {pct(x.confirmed_overlap)}
                      {x.status === 'lower_bound' ? ` (up to ${pct(x.upper_bound)})` : ''}
                    </span>
                    <span className="phc-small">
                      {x.common_count} shared compan{x.common_count === 1 ? 'y' : 'ies'}
                    </span>
                    <span className="phc-bar" aria-hidden="true">
                      <span style={{ width: `${Math.min(100, x.confirmed_overlap * 100)}%` }} />
                    </span>
                  </li>
                ))}
              </ol>

              {result.matrix.order.length > 1 && (
                <div className="phc-matrix-block">
                  <h4 className="phc-h4">Pairwise overlap matrix</h4>
                  <div className="phc-scroll">
                    <table className="phc-matrix">
                      <thead>
                        <tr>
                          <th scope="col">
                            <span className="phc-sr">Fund</span>
                          </th>
                          {result.matrix.order.map((k, i) => (
                            <th key={k} scope="col" title={name(k)}>
                              {i + 1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.matrix.order.map((k, i) => (
                          <tr key={k}>
                            <th scope="row">
                              {i + 1}. {name(k)}
                            </th>
                            {result.matrix.values[i].map((v, j) => (
                              <td
                                key={j}
                                className={result.matrix.status[i][j] === 'self' ? 'phc-self' : ''}
                                style={
                                  result.matrix.status[i][j] === 'self'
                                    ? undefined
                                    : { '--heat': Math.min(1, v * 2) }
                                }
                              >
                                {result.matrix.status[i][j] === 'self'
                                  ? '—'
                                  : `${pct(v, 0)}${result.matrix.status[i][j] === 'lower_bound' ? '+' : ''}`}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="phc-small">“+” means some holdings could not be identified, so the true overlap may be higher.</p>
                </div>
              )}

              <div className="phc-pair-selector-block">
                <h4 className="phc-h4">Fund-pair comparison</h4>
                <select
                  className="phc-input"
                  value={effectiveOpenPair || ''}
                  disabled={isClient}
                  style={isClient ? { cursor: 'default', background: '#faf8ef' } : undefined}
                  onChange={(e) => {
                    if (isClient) return;
                    setOpenPair(e.target.value);
                    onUiChange?.({ openPair: e.target.value });
                  }}
                  aria-label="Choose a fund pair"
                >
                  {pairs.map((x) => (
                    <option key={`${x.a}|${x.b}`} value={`${x.a}|${x.b}`}>
                      {name(x.a)} vs {name(x.b)}
                    </option>
                  ))}
                </select>
                {pairs
                  .filter((x) => `${x.a}|${x.b}` === effectiveOpenPair)
                  .map((x) => (
                    <div key="pair" className="phc-pair-detail">
                      <p className="phc-muted">
                        {pct(x.confirmed_overlap)} confirmed overlap · {x.common_count} shared companies
                        {x.date_gap_days ? ` · disclosure dates ${x.date_gap_days} days apart` : ''}
                      </p>
                      <div className="phc-scroll">
                        <table className="phc-table">
                          <thead>
                            <tr>
                              <th>Shared company</th>
                              <th>{name(x.a)}</th>
                              <th>{name(x.b)}</th>
                              <th>Counts toward overlap</th>
                            </tr>
                          </thead>
                          <tbody>
                            {x.shared.slice(0, 25).map((s) => (
                              <tr key={s.identity_id}>
                                <th scope="row">{s.display_name}</th>
                                <td>{pct(s.weights[x.a])}</td>
                                <td>{pct(s.weights[x.b])}</td>
                                <td>{pct(s.overlap_contribution)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {x.distinct_a && (
                        <p className="phc-small">
                          Only in {name(x.a)}: {x.distinct_a.count} companies ({pct(x.distinct_a.weight)}). Only in {name(x.b)}: {x.distinct_b.count} companies ({pct(x.distinct_b.weight)}).
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </details>
        )}

        {/* 2. Company & sector exposure */}
        <details
          className="phc-card phc-accordion-card"
          open={openAccordions.companySector !== undefined ? openAccordions.companySector : false}
          onToggle={(e) => handleToggleAccordion('companySector', e.target.open)}
        >
          <summary
            className="phc-accordion-summary"
            onClick={(e) => { if (isClient) e.preventDefault(); }}
            style={isClient ? { cursor: 'default' } : undefined}
          >
            <span>Company &amp; sector exposure</span>
            <span className="phc-accordion-indicator">▾</span>
          </summary>
          <div className="phc-accordion-content">
            {p ? (
              <div className="phc-grid2">
                <div>
                  <h4 className="phc-h4">Company exposure across the portfolio</h4>
                  <ul className="phc-exposures">
                    {p.exposures.slice(0, 15).map((e) => (
                      <li key={e.identity_id}>
                        <span>{e.display_name}</span>
                        <span className="phc-num">
                          {pct(e.exposure)} · {inr(e.amount)}
                        </span>
                        <span className="phc-small">
                          {e.contributors.length > 1
                            ? `via ${e.contributors.length} funds`
                            : `only in ${name(e.contributors[0].fund_key)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {p.unresolved_exposure > 0 && (
                    <p className="phc-small">
                      {pct(p.unresolved_exposure)} of value sits in holdings that could not be named.
                    </p>
                  )}
                </div>
                <div>
                  <h4 className="phc-h4">Sector exposure</h4>
                  {p.sectors ? (
                    <ul className="phc-exposures">
                      {p.sectors.slice(0, 10).map((s) => (
                        <li key={s.name}>
                          <span>{s.name}</span>
                          <span className="phc-num">{pct(s.exposure)}</span>
                          <span className="phc-bar" aria-hidden="true">
                            <span style={{ width: `${Math.min(100, s.exposure * 100)}%` }} />
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="phc-muted">Sector data is not available for every fund.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="phc-alert">
                {result.portfolio_unavailable_reason || 'Portfolio-weighted exposure is unavailable because some values are missing.'}
              </p>
            )}
          </div>
        </details>

        {/* 3. Companies only one fund holds */}
        <details
          className="phc-card phc-accordion-card"
          open={openAccordions.singleFund !== undefined ? openAccordions.singleFund : false}
          onToggle={(e) => handleToggleAccordion('singleFund', e.target.open)}
        >
          <summary
            className="phc-accordion-summary"
            onClick={(e) => { if (isClient) e.preventDefault(); }}
            style={isClient ? { cursor: 'default' } : undefined}
          >
            <span>Companies only one fund holds</span>
            <span className="phc-accordion-indicator">▾</span>
          </summary>
          <div className="phc-accordion-content">
            <h4 className="phc-h4">What each fund adds</h4>
            <div className="phc-scroll">
              <table className="phc-table">
                <thead>
                  <tr>
                    <th>Fund</th>
                    <th>Disclosed</th>
                    <th>Only in this fund</th>
                    <th>Shared with others</th>
                    <th>Not identified</th>
                  </tr>
                </thead>
                <tbody>
                  {result.contributions.map((c) => {
                    const f = result.funds.find((x) => x.fund_key === c.fund_key);
                    return (
                      <tr key={c.fund_key}>
                        <th scope="row">{name(c.fund_key)}</th>
                        <td>{f && f.disclosure_asof ? f.disclosure_asof : '—'}</td>
                        <td>
                          {pct(c.unique_named_weight)} · {c.unique_count}
                        </td>
                        <td>
                          {pct(c.shared_named_weight)} · {c.shared_count}
                        </td>
                        <td>{pct(c.unresolved_weight)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {singleFundCompanies.length > 0 && (
              <div className="phc-single-fund-breakdown">
                <h4 className="phc-h4" style={{ marginTop: '1.25rem' }}>
                  Distinct holdings ({singleFundCompanies.length} companies held in only one fund · {inr(singleFundExposureAmount)})
                </h4>
                <ul className="phc-exposures">
                  {singleFundCompanies.slice(0, 10).map((e) => (
                    <li key={e.identity_id}>
                      <span>{e.display_name}</span>
                      <span className="phc-num">
                        {pct(e.exposure)} · {inr(e.amount)}
                      </span>
                      <span className="phc-small">
                        only in {name(e.contributors[0]?.fund_key)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </details>

        {/* Coverage and limitations */}
        {(notCompared.length > 0 || blocking.length > 0) && (
          <details
            className="phc-card phc-accordion-card"
            open={openAccordions.coverage !== undefined ? openAccordions.coverage : notCompared.some((n) => n.kind === 'holdings_unavailable')}
            onToggle={(e) => handleToggleAccordion('coverage', e.target.open)}
          >
            <summary
              className="phc-accordion-summary"
              onClick={(e) => { if (isClient) e.preventDefault(); }}
              style={isClient ? { cursor: 'default' } : undefined}
            >
              <span>Data coverage and limitations · {notCompared.length + blocking.length}</span>
              <span className="phc-accordion-indicator">▾</span>
            </summary>
            <div className="phc-accordion-content">
              <ul className="phc-list">
                {notCompared.map((n, i) => (
                  <li key={i} className="phc-holding">
                    <div className="phc-holding__main">
                      <p className="phc-holding__name">{n.name}</p>
                      <p className="phc-small">{n.reason}</p>
                    </div>
                    <div className="phc-holding__value">
                      <span className="phc-num">{n.value != null ? inr(n.value) : '—'}</span>
                    </div>
                  </li>
                ))}
              </ul>
              {blocking.map((i, k) => (
                <p key={k} className="phc-small">
                  {i.message}
                </p>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
